/**
 * Pure helpers for `/screen-print` — the screen-print RUN worklist.
 *
 * A "run" is a FILMLESS batch (`kind: 'screen-print'`): zero gang-sheet items, one or more screen-print
 * placements, born READY because there is no film to generate. It is the same batch record a gang sheet
 * uses, so everything here reads `BatchSummary` — no new endpoint.
 *
 * ⚠️ `kind` is ABSENT on every batch built before the field existed, and absent means `'gang-sheet'`
 * (BatchKindEnum) — never "unknown". Both lists below therefore test for the string explicitly rather
 * than branching on presence, so the ~22 live legacy sheets stay on `/batches` where they belong.
 */
import type { components } from '@/lib/api/types.gen';

export type BatchSummary = components['schemas']['BatchSummary'];
export type ScreenPrintWorkItem = NonNullable<BatchSummary['screenPrintWork']>[number];

/** Is this batch a filmless screen-print run? Absent `kind` ⇒ gang sheet. */
export function isScreenPrintRun(b: Pick<BatchSummary, 'kind'>): boolean {
  return b.kind === 'screen-print';
}

/** The `/screen-print` list: runs only. */
export function screenPrintRuns(batches: readonly BatchSummary[]): BatchSummary[] {
  return batches.filter(isScreenPrintRun);
}

/** The `/batches` list: everything that owns a film. The two are a partition — no batch is on both. */
export function gangSheetBatches(batches: readonly BatchSummary[]): BatchSummary[] {
  return batches.filter((b) => !isScreenPrintRun(b));
}

/**
 * Where a freshly built batch LIVES. `/batches` and `/screen-print` are a PARTITION — `/batches`
 * renders `gangSheetBatches(...)` and filters runs out by construction — so landing on the wrong one
 * shows the operator a list that cannot contain what they just built, which reads as "the build
 * failed". Absent `kind` ⇒ gang sheet, the same rule as {@link isScreenPrintRun}, so every legacy
 * batch keeps landing exactly where it does today.
 */
export function buildLandingRoute(batch: Pick<BatchSummary, 'kind'>): '/screen-print' | '/batches' {
  return isScreenPrintRun(batch) ? '/screen-print' : '/batches';
}

/** One press setup on a run — i.e. ONE SCREEN: a design, at a location, in a size band. */
export type RunSetup = {
  /** Unique, stable row identity (`<design>|<location>|<band>`). Safe as a React key. */
  key: string;
  location: string;
  sizeBand: ScreenPrintWorkItem['sizeBand'];
  designId?: string;
  designName?: string;
  /** Garments through THIS screen — Σ quantity across every line sharing it. */
  garments: number;
  pressed: number;
};

/** A placement whose design never resolved. Groups on its own — never into a real design. Cannot
 *  collide with a resolved key: those are always prefixed `id:` or `name:`. */
const UNKNOWN_DESIGN = 'unknown';

/**
 * The design half of a screen's identity.
 *
 * `designId` is canonical. `designName` is a fallback for rows written before the id was populated —
 * the server sets the two TOGETHER today (`dtf-line-resolver.ts` only reads a name once the id
 * resolved), so on current data that branch is inert and exists for legacy/hand-built work items.
 * Both absent ⇒ {@link UNKNOWN_DESIGN}: one "design unknown" row per (location, band), never folded
 * into a named design's screen.
 */
function designKeyOf(w: ScreenPrintWorkItem): string {
  if (w.designId) return `id:${w.designId}`;
  if (w.designName) return `name:${w.designName}`;
  return UNKNOWN_DESIGN;
}

/**
 * Group a run's placements into the SCREENS it will burn — keyed `(design, location, sizeBand)`.
 *
 * A setup IS a screen: one design, at one location, at one art size. That is what gets burned, racked
 * and set up, so it is the only fold whose count an operator can act on.
 *
 * ⚠️ This used to key on `location` ALONE, which under-reported every run that mixes art: two designs
 * both printed Front collapsed into one row — `Front · A, B` — with one merged `pressed/garments`
 * counter. Two designs over two locations is THREE burns and the old fold said two, and the merged
 * counter could not distinguish "A is finished" from "both are half done". Written up as § 11 of
 * `docs/screen-print-production-lane-plan-v1.md`; nothing refuses a multi-design run, and the shop
 * deliberately batches drops that share a blank and a due date, so the fix is to COUNT it honestly
 * rather than forbid it.
 *
 * ⚠️ `sizeBand` is in the key because youth art is a separate burn — the printed-transfer shelf
 * already keys `(designId, band)` for the same reason. Owner decision, 2026-09-11. The error is
 * asymmetric and that is why the key is the wider one: an extra row costs a glance, a missing row
 * stalls the run mid-press with a screen nobody burned.
 */
export function runSetups(work: readonly ScreenPrintWorkItem[]): RunSetup[] {
  const by = new Map<string, RunSetup>();
  for (const w of work) {
    const key = `${designKeyOf(w)}|${w.location}|${w.sizeBand}`;
    let s = by.get(key);
    if (!s) {
      s = {
        key,
        location: w.location,
        sizeBand: w.sizeBand,
        designId: w.designId,
        designName: w.designName,
        garments: 0,
        pressed: 0,
      };
      by.set(key, s);
    }
    // A design RENAMED between two lines still keys on its id, so keep the first name we saw rather
    // than letting row identity depend on iteration order.
    if (!s.designName && w.designName) s.designName = w.designName;
    s.garments += w.quantity;
    if (w.pressedAt) s.pressed += w.quantity;
  }
  return [...by.values()].sort(
    (a, z) =>
      designLabel(a).localeCompare(designLabel(z)) ||
      a.location.localeCompare(z.location) ||
      a.sizeBand.localeCompare(z.sizeBand),
  );
}

/** What to CALL a screen's design — the name, else the raw id, else an explicit unknown. */
export function designLabel(s: Pick<RunSetup, 'designId' | 'designName'>): string {
  return s.designName ?? s.designId ?? 'design unknown';
}

/**
 * Garments on a run — one per LINE, not per placement.
 *
 * ⚠️ A front+back line is TWO placements and ONE shirt. Summing `quantity` across placements is the
 * over-count that would tell the floor to pull twice the blanks; the pull list has the same rule and a
 * test at front+back qty≥2 where the two folds visibly differ.
 */
export function runGarmentCount(work: readonly ScreenPrintWorkItem[]): number {
  const byLine = new Map<string, number>();
  for (const w of work) {
    const key = `${w.storeId}#${w.orderId}#${w.lineId}`;
    if (!byLine.has(key)) byLine.set(key, w.quantity);
  }
  return [...byLine.values()].reduce((a, b) => a + b, 0);
}

/** Every placement pressed? Drives the run's "ready to close" state. */
export function runFullyPressed(work: readonly ScreenPrintWorkItem[]): boolean {
  return work.length > 0 && work.every((w) => Boolean(w.pressedAt));
}

/**
 * What the Build action will actually produce for the current selection — so the button and the dialog
 * can say it instead of always claiming "gang sheet".
 *
 * ⚠️ Reported by the owner from the live dashboard: selecting a screen-print line offered "Build gang
 * sheet", which is not what happens and not what the server will accept. The copy has to follow the
 * selection.
 *
 * `mixed` is not a third product — it is the D2 refusal (a screen-print run is built ALONE), so the UI
 * says so BEFORE the click rather than surfacing a 422 after it.
 */
export type BuildKind = 'gang-sheet' | 'screen-print' | 'mixed' | 'none';

export function buildKindFor(
  lines: readonly { decoration?: 'dtf' | 'screen-print' | 'mixed' }[],
): BuildKind {
  if (lines.length === 0) return 'none';
  // A MIXED line has film, so it builds a gang sheet like any DTF line — only a wholly screen-print
  // line is filmless. This mirrors the server, which keys on "resolved zero DTF items".
  const filmless = lines.filter((l) => l.decoration === 'screen-print').length;
  if (filmless === 0) return 'gang-sheet';
  if (filmless === lines.length) return 'screen-print';
  return 'mixed';
}

/** Button/dialog copy for each outcome. */
export const BUILD_COPY: Record<BuildKind, { cta: string; title: string; blurb: string }> = {
  'gang-sheet': {
    cta: 'Build gang sheet',
    title: 'Build gang sheet',
    blurb: 'Nest the selected lines onto a film roll.',
  },
  'screen-print': {
    cta: 'Build screen-print run',
    title: 'Build screen-print run',
    blurb:
      'These are printed off the gang sheet, so there is no film and no roll to pick. The run is ready to press as soon as it is built.',
  },
  mixed: {
    cta: 'Build',
    title: 'Screen print and film in one selection',
    blurb:
      'A screen-print run is built on its own. Drop one side of the selection — filter by print method and select just those lines.',
  },
  none: { cta: 'Build', title: 'Build', blurb: '' },
};
